import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Typography, Button, Space, DatePicker } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import intercompanyService from '@/services/intercompany.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Paragraph, Text } = Typography;

const IntercompanyBalanceReportPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());
  const asOfStr = asOf.format('YYYY-MM-DD');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['intercompany-balance', asOfStr],
    queryFn: () => intercompanyService.balanceReport(asOfStr),
  });

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>{t('intercompanyBalance')}</Title>
      <Paragraph type="secondary">{t('intercompanyBalanceSubtitle')}</Paragraph>
      <Card
        title={
          <Space>
            <Text type="secondary">{t('asAt')}</Text>
            <DatePicker value={asOf} onChange={(d) => d && setAsOf(d)} format="DD-MM-YYYY" allowClear={false} />
          </Space>
        }
        extra={
          <Space wrap>
            <ReportDocumentActions reportKey="intercompany-balance" params={{ as_of: asOfStr }} />
            <Button onClick={() => navigate(`/fin/reports/intercompany-statement?as_of=${asOfStr}`)}>
              {t('statementAging')}
            </Button>
            <Button onClick={() => navigate('/fin/intercompany/settlements')}>{t('settlements')}</Button>
            <Button onClick={() => navigate('/fin/intercompany/transactions')}>{t('viewTransactions')}</Button>
          </Space>
        }
      >
        <Table
          loading={isLoading}
          rowKey="affiliate_company_id"
          dataSource={rows}
          columns={[
            { title: t('affiliateCompany'), dataIndex: 'affiliate_company_name' },
            {
              title: t('dueFromGroup'),
              dataIndex: 'due_from_balance',
              align: 'right',
              render: (v) => formatCurrency(Number(v)),
            },
            {
              title: t('dueToGroup'),
              dataIndex: 'due_to_balance',
              align: 'right',
              render: (v) => formatCurrency(Number(v)),
            },
            {
              title: t('netFromMinusTo'),
              dataIndex: 'net_balance',
              align: 'right',
              render: (v) => formatCurrency(Number(v)),
            },
            {
              title: '',
              key: 'stmt',
              render: (_: unknown, r: { affiliate_company_id: string }) => (
                <Button
                  size="small"
                  type="link"
                  onClick={() =>
                    navigate(`/fin/reports/intercompany-statement?affiliate=${r.affiliate_company_id}&as_of=${asOfStr}`)
                  }
                >
                  {t('statement')}
                </Button>
              ),
            },
          ]}
          locale={{ emptyText: t('noPostedIcBalancesYet') }}
        />
      </Card>
    </div>
  );
};

export default IntercompanyBalanceReportPage;
